import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchDomains, createDomain, verifyDomain, deleteDomain, fetchSiteResolve } from '../api/domains.js';
import type { CreateDomainRequest } from '@shared/api/contracts/domains.js';

const DOMAINS_QK = ['domains', 'list'] as const;
const SITE_RESOLVE_QK = ['site', 'resolve'] as const;

export function useDomains() {
  return useQuery({
    queryKey: DOMAINS_QK,
    queryFn: ({ signal }) => fetchDomains(signal),
  });
}

export function useCreateDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDomainRequest) => createDomain(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: DOMAINS_QK }),
  });
}

export function useVerifyDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => verifyDomain(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: DOMAINS_QK }),
  });
}

export function useDeleteDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteDomain(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: DOMAINS_QK }),
  });
}

/**
 * Resolves the current custom-domain host to its tenant/league context.
 * This query is enabled only on non-canonical hosts.
 */
export function useSiteResolve(enabled: boolean) {
  return useQuery({
    queryKey: SITE_RESOLVE_QK,
    queryFn: ({ signal }) => fetchSiteResolve(signal),
    enabled,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 min — domain mappings don't change often
  });
}
